import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import AuthLayout from '../../components/auth/AuthLayout';
import { useNotification } from '../../context/NotificationContext';
import { authService } from '../../services/api/authService';

const EmailDisplay = styled(Box)`
  background: #1a1f24;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #42515E;
  margin: 16px 0;
  text-align: center;
`;

export default function GuestResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');
  const userId = searchParams.get('userId');
  const email = searchParams.get('email');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!token || !userId) {
      setError('Invalid reset link');
      return;
    }

    try {
      setLoading(true);
      await authService.resetGuestPassword(token, userId, password);
      showNotification('Password set successfully! You can now log in to your account.', 'success');
      navigate('/login');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !userId || !email) {
    return (
      <AuthLayout>
        <Alert severity="error">
          Invalid reset link. Please check your email for the correct link.
        </Alert>
        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2, background: '#2563EB', color: '#fff', fontWeight: 700, borderRadius: 2, '&:hover': { background: '#1d4ed8' } }}
        >
          Back to Login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1, textAlign: 'center', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Set Your Password
      </Typography>
      <Box sx={{ mb: 3, color: '#b0b8c1', textAlign: 'center', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
        Welcome to Bellhop Express! Please set a password for your account.
      </Box>
      
      <EmailDisplay>
        <Typography variant="body2" sx={{ color: '#b0b8c1', mb: 1 }}>
          Your email address:
        </Typography>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
          {decodeURIComponent(email)}
        </Typography>
      </EmailDisplay>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          margin="normal"
          required
          fullWidth
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          inputProps={{ minLength: 8 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#4B5563' },
              '&:hover fieldset': { borderColor: '#2563EB' },
              '&.Mui-focused fieldset': { borderColor: '#2563EB' },
            },
            '& .MuiInputLabel-root': { color: '#9CA3AF' },
            '& .MuiInputBase-input': { color: '#fff' },
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          inputProps={{ minLength: 8 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#4B5563' },
              '&:hover fieldset': { borderColor: '#2563EB' },
              '&.Mui-focused fieldset': { borderColor: '#2563EB' },
            },
            '& .MuiInputLabel-root': { color: '#9CA3AF' },
            '& .MuiInputBase-input': { color: '#fff' },
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 3, mb: 2, background: '#2563EB', color: '#fff', fontWeight: 700, borderRadius: 2, '&:hover': { background: '#1d4ed8' } }}
        >
          {loading ? 'Setting Password...' : 'Set Password'}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => navigate('/login')}
          sx={{ color: '#2563EB', textTransform: 'none' }}
        >
          Back to Login
        </Button>
      </Box>
    </AuthLayout>
  );
} 